/**
 * Run Q / Q-4 — the sidebar's item set, as data.
 *
 * It was inline in Sidebar.tsx, which meant "what does a clinician see?" could
 * only be answered by reading JSX. Q-4 adds a module flag to that decision and
 * Q-5 changes the grouping, so it becomes a pure function with a pinned
 * contract instead.
 */
export type NavIcon =
  | 'dashboard' | 'patients' | 'frontdesk' | 'insights'
  | 'exercises' | 'conditions' | 'settings' | 'protocols' | 'audit' | 'shield' | 'kpi';

export interface NavEntry {
  to: string;
  icon: NavIcon;
  /** i18n key, with the English default the Sidebar passes to t(). */
  labelKey: string;
  defaultLabel: string;
}
export interface NavGroupModel { label: string; items: NavEntry[] }

export interface NavInput {
  role: string | undefined;
  features: { isCheckerOnly: boolean; hasFullClinical: boolean; hasAnalytics: boolean; hasExerciseLibrary: boolean };
  modules: { frontDesk: boolean };
  /**
   * KPI K1-2 — the clinic-scoped OWNER flag (`users.is_clinic_owner`, carried
   * on the GET /api/auth/me payload). NOT a role: the KPI spec asked for a
   * fifth role `owner`, and the operator ordered a flag instead because
   * `requireRole` on the server is hierarchical and a fifth role would leak
   * into every existing comparison. Absent means "not the owner", so every
   * existing caller of this model keeps exactly the nav it had.
   */
  isClinicOwner?: boolean;
  /** KPI R3 — this user may see the staff board (the owner, or flagged by the owner; GET /api/auth/me). A UI hint: the server answers 404 to everyone else. */
  staffBoardVisible?: boolean;
}

export function buildNavGroups(input: NavInput): NavGroupModel[] {
  const { role, features, modules } = input;
  const isClinicOwner = input.isClinicOwner === true;
  const isSuperAdmin = role === 'super_admin';

  const main: NavEntry[] = [
    { to: '/dashboard', icon: 'dashboard', labelKey: 'nav.dashboard', defaultLabel: 'Dashboard' },
  ];
  if (features.hasFullClinical) {
    main.push({ to: '/patients', icon: 'patients', labelKey: 'nav.patients', defaultLabel: 'Patients' });
    // Q-4 — Front Desk only when the clinic has the module on. Visibility
    // WITHIN the module stays server-side (admin + front desk see all, a
    // clinician sees their own patients); this is whether it exists at all.
    if (modules.frontDesk) {
      main.push({ to: '/front-desk', icon: 'frontdesk', labelKey: 'nav.frontdesk', defaultLabel: 'Front Desk' });
    }
    // KPI R3 — the referral-source gap list (with patient names) is the owner's
    // and the front desk's only; the server answers 404 to everyone else.
    if (role === 'front_desk' || isClinicOwner) {
      main.push({ to: '/referral-gaps', icon: 'patients', labelKey: 'nav.referralGaps', defaultLabel: 'Referral gaps' });
    }
  }
  // KPI R3 — the staff board, for the owner and the users the owner flags.
  if (input.staffBoardVisible === true) {
    main.push({ to: '/staff-board', icon: 'insights', labelKey: 'nav.staffBoard', defaultLabel: 'Staff board' });
  }
  if (features.hasAnalytics) {
    main.push({ to: '/analytics', icon: 'insights', labelKey: 'nav.analytics', defaultLabel: 'Analytics' });
  }
  // K1-2 — the KPI Portal is the clinic OWNER's, and only theirs: the spec's
  // Access control section says admin, front desk and clinician "must not see
  // the KPI Portal: no menu item". Hiding it is cosmetic, not the control —
  // every /api/kpi route answers 404 to anyone without the flag, whatever the
  // browser renders.
  if (isClinicOwner) {
    main.push({ to: '/kpi', icon: 'kpi', labelKey: 'nav.kpi', defaultLabel: 'KPI Portal' });
  }

  const groups: NavGroupModel[] = [{ label: 'MAIN', items: main }];

  if (features.hasFullClinical) {
    const library: NavEntry[] = [];
    if (features.hasExerciseLibrary) library.push({ to: '/exercises', icon: 'exercises', labelKey: 'nav.exercises', defaultLabel: 'Exercises' });
    library.push({ to: '/conditions', icon: 'conditions', labelKey: 'nav.conditions', defaultLabel: 'Conditions' });
    if (library.length > 0) groups.push({ label: 'LIBRARY', items: library });
  }

  // Q-5 — ONE settings entry. Integrations, the Audit Log and Data & Privacy
  // used to be three more sidebar destinations (the last one labelled "GDPR
  // Requests", for a page with no request queue). They are sections inside
  // Settings now, so the sidebar is the clinical spine plus one door to
  // configuration. The old URLs redirect — see App.tsx.
  groups.push({
    label: 'SETTINGS',
    items: [{ to: '/settings/clinic', icon: 'settings', labelKey: 'nav.settings', defaultLabel: 'Settings' }],
  });

  // Platform-level, not clinic-level: the super admin console stays its own
  // destination because it crosses clinics.
  if (isSuperAdmin) {
    groups.push({ label: 'PLATFORM', items: [{ to: '/superadmin', icon: 'shield', labelKey: 'nav.superadmin', defaultLabel: 'SuperAdmin' }] });
  }

  return groups;
}

/** Flat list of destinations, for the "what can this role reach?" pins. */
export function navDestinations(input: NavInput): string[] {
  return buildNavGroups(input).flatMap((g) => g.items.map((i) => i.to));
}
